import { toSorted } from "@/utils/array/to-sorted";
import { z } from "zod";

//-------------------------------------------------------
//-------------------- ZOD SCHEMA -----------------------
//-------------------------------------------------------
// This is a recursive type, see https://zod.dev/?id=recursive-types for more info on pattern.
const baseAnswerSchema = z.object({
  id: z.number(),
  priority: z.number(),
  task_id: z.number(),
  text: z.string(),
});

type Answer = z.infer<typeof baseAnswerSchema> & {
  questions: z.infer<typeof questionsSchema> | null;
};

const answerSchema: z.ZodType<Answer> = baseAnswerSchema.extend({
  questions: z.lazy(() => questionsSchema).nullable(),
});

const questionSchema = z.object({
  answers: z.record(z.string(), answerSchema),
  display_order: z.number(),
  id: z.number(),
  text: z.string(),
});

export type Question = z.infer<typeof questionSchema>;
export const questionsSchema = z.record(z.string(), questionSchema);
export type Questions = z.infer<typeof questionsSchema>;
//-------------------------------------------------------
//
export function createQNATree(data: unknown) {
  const qnaRaw = questionsSchema.parse(data);
  const qna: QuestionNode[] = [];

  // Helper function to build the tree recursively
  function buildQuestionNode(
    questionRaw: z.infer<typeof questionSchema>
  ): QuestionNode {
    const answers: AnswerNode[] = [];
    const answersRawSorted = toSorted(
      Object.values(questionRaw.answers),
      (a, b) => a.priority - b.priority
    );
    for (const answerRaw of answersRawSorted) {
      let questions: QuestionNode[] = [];
      if (answerRaw.questions) {
        // Get all questions sorted by display_order
        const questionsInnerRawSorted = toSorted(
          Object.values(answerRaw.questions),
          (a, b) => a.display_order - b.display_order
        );

        questions = questionsInnerRawSorted.map((questionInnerRaw) =>
          buildQuestionNode(questionInnerRaw)
        );
      }

      answers.push(
        new AnswerNode(
          answerRaw.id,
          answerRaw.task_id,
          answerRaw.text,
          questions
        )
      );
    }

    return new QuestionNode(questionRaw.id, questionRaw.text, answers);
  }

  // Build root questions
  for (const questionRaw of toSorted(
    Object.values(qnaRaw),
    (a, b) => a.display_order - b.display_order
  )) {
    qna.push(buildQuestionNode(questionRaw));
  }

  return AnswerNode.root(qna);
}

export type QNATree = ReturnType<typeof createQNATree>;

/**
 * NOTE: the `priority`, and `display_order` is embeded into the array order
 */
class AnswerNode {
  readonly isRoot: boolean;

  readonly id: number;
  readonly taskId: number;
  readonly text: string;
  readonly questions: QuestionNode[];

  isChoosen: boolean;

  constructor(
    id: number,
    taskId: number,
    text: string,
    questions: QuestionNode[] = [],
    isRoot = false
  ) {
    this.isRoot = isRoot;
    this.id = id;
    this.taskId = taskId;
    this.text = text;
    this.questions = questions;
    this.isChoosen = isRoot;
  }

  /**
   * reset only current branch of tree, not walking further
   */
  reset() {
    this.isChoosen = false;
    this.questions.forEach((q) => q.reset());
  }

  /**
   * reset also other branch that come after this until end of walk.
   * @param {boolean} forceReset - used internally for recursion, if given will reset current branch
   */
  resetFrom(questionId: number, forceReset?: boolean): boolean {
    let shouldReset = forceReset ?? false;
    for (const q of this.questions) {
      shouldReset = q.resetFrom(questionId, shouldReset);
    }

    return shouldReset;
  }

  choose() {
    this.isChoosen = true;
  }

  getQuestionById(id: number): QuestionNode | undefined {
    for (const q of this.questions) {
      const found = q.getQuestionById(id);
      if (found) {
        return found;
      }
    }
  }

  /**
   * returns an array of questions to answer/answered by order
   * you should never get questions not answered beween two answered one, e.g.: [answered, to-answer, to-answer, answered, to-answer]
   */
  getQuestionsChain(): QuestionNode[] {
    const questions: QuestionNode[] = [];
    for (const q of this.questions) {
      questions.push(q);
      questions.push(...q.getQuestionsChain());
    }
    return questions.filter(Boolean);
  }

  get firstUnansweredQuestion(): QuestionNode | undefined {
    for (const q of this.questions) {
      const found = q.firstUnansweredQuestion;
      if (found) return found;
    }
  }

  /**
   * get the potentail max unanswered questions in this tree
   */
  get maxUnansweredQuestions(): number {
    return Math.max(0, ...this.questions.map((q) => q.maxUnansweredQuestions));
  }

  /**
   * Helper to build a root node.
   */
  static root(questions: QuestionNode[] = []): AnswerNode {
    return new AnswerNode(-1, -1, "", questions, true);
  }
}

class QuestionNode {
  readonly id: number;
  readonly text: string;
  readonly answers: AnswerNode[];

  constructor(id: number, text: string, answers: AnswerNode[] = []) {
    this.id = id;
    this.text = text;
    this.answers = answers;
  }

  get isAnswered() {
    return Boolean(this.answer);
  }

  get answer(): AnswerNode | undefined {
    return this.answers.find((a) => a.isChoosen);
  }

  /**
   * `undefined` means all questions has been answered.
   */
  get firstUnansweredQuestion(): QuestionNode | undefined {
    if (!this.isAnswered) {
      return this;
    }
    return this.answer!.firstUnansweredQuestion;
  }

  /**
   * get the potentail max unanswered questions in this tree
   */
  get maxUnansweredQuestions(): number {
    // if we answer this question, we don't count ourself
    // and only ask the depth of the answer
    // else we need to ask everything and add ourself
    if (this.isAnswered) {
      return this.answer!.maxUnansweredQuestions;
    }
    return (
      1 + Math.max(0, ...this.answers.map((a) => a.maxUnansweredQuestions))
    );
  }

  getQuestionById(id: number): QuestionNode | undefined {
    if (this.id === id) return this;
    for (const a of this.answers) {
      const found = a.getQuestionById(id);
      if (found) {
        return found;
      }
    }
  }

  /**
   * returns an array of questions answered by order
   */
  getQuestionsChain(): QuestionNode[] {
    return this.answer?.getQuestionsChain() ?? [];
  }

  /**
   * @throws in case of bad id
   */
  choose(id: number) {
    const a = this.answers.find((a) => a.id === id);
    if (!a) {
      throw new Error(
        "Bad Answer ID: you can only choose form existing answers"
      );
    }
    a.choose();
  }

  /**
   * reset only current branch of tree, not walking further
   */
  reset() {
    this.answers.forEach((a) => a.reset());
  }

  /**
   * reset also other branch that come after this until end of walk.
   * @param {boolean} forceReset - used internally for recursion, if given will reset current branch
   */
  resetFrom(questionId: number, forceReset?: boolean): boolean {
    if (forceReset || this.id === questionId) {
      this.reset();
      return true;
    } else if (this.isAnswered) {
      return this.answer!.resetFrom(questionId);
    }
    return false;
  }
}
